import React from 'react';
import { Icon } from '@/components/ui/Icon';
import styles from './ErrorBoundary.module.css';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      copied: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught unhandled error:', error, errorInfo);

    // Auto-detect chunk load failure after deployments/restarts
    const isChunkError =
      error?.message?.includes('dynamically imported module') ||
      error?.message?.includes('Failed to fetch dynamically imported') ||
      error?.message?.includes('Loading chunk');

    if (isChunkError && typeof window !== 'undefined') {
      const RETRY_KEY = 'titanium_chunk_retry_timestamp';
      const lastReload = Number(sessionStorage.getItem(RETRY_KEY) || '0');
      const now = Date.now();
      if (now - lastReload > 10000) {
        sessionStorage.setItem(RETRY_KEY, String(now));
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleResetSettingsAndReload = () => {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('te_admin_v2');
        localStorage.removeItem('te_admin_v1');
        localStorage.removeItem('user');
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
    } catch (e) {}
    window.location.href = '/';
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  handleCopyDetails = () => {
    const error = this.state.error;
    const currentRoute = typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : 'Unknown';
    const buildTime = typeof __APP_BUILD_TIME__ !== 'undefined'
      ? __APP_BUILD_TIME__
      : 'Dev Mode';

    const details = [
      `Error: ${error?.message || String(error)}`,
      `Route: ${currentRoute}`,
      `Build Time: ${buildTime}`,
      '',
      'Stack Trace:',
      error?.stack || 'No stack trace available'
    ].join('\n');

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(details).then(() => {
        this.setState({ copied: true });
        setTimeout(() => this.setState({ copied: false }), 2500);
      }).catch(() => {});
    }
  };

  render() {
    if (this.state.hasError) {
      const error = this.state.error;
      const isChunkError =
        error?.message?.includes('dynamically imported module') ||
        error?.message?.includes('Failed to fetch dynamically imported');

      const currentRoute = typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '';

      const isRoute = this.props.isRoute;

      return (
        <div className={isRoute ? styles.routeContainer : styles.container}>
          <div className={styles.card}>
            <span className={styles.badge}>
              {isChunkError ? 'Update Detected' : 'Application Exception'}
            </span>

            <h1 className={styles.title}>Something went wrong</h1>

            {/* Error Message FIRST above stack */}
            <div className={styles.errorMessageBlock}>
              <strong>Error:</strong> {error?.message || 'An unexpected rendering error occurred.'}
            </div>

            {currentRoute && (
              <div className={styles.routeTag}>
                <span>Route:</span> <code>{currentRoute}</code>
              </div>
            )}

            <p className={styles.description}>
              {isChunkError
                ? 'A new version of the application was deployed. Please reload the page to get the latest update.'
                : 'The application encountered an unexpected issue while rendering this view.'}
            </p>

            <div className={styles.actions}>
              {isRoute ? (
                <button
                  type="button"
                  className={styles.reloadBtn}
                  onClick={this.handleReset}
                >
                  Try Again
                </button>
              ) : null}

              <button
                type="button"
                className={isRoute ? styles.homeBtn : styles.reloadBtn}
                onClick={this.handleReload}
              >
                Reload Page
              </button>

              <button
                type="button"
                className={styles.copyBtn}
                onClick={this.handleResetSettingsAndReload}
                style={{ borderColor: 'var(--blush-border, #fca5a5)' }}
                title="Clears local storage, admin state and reloads"
              >
                <Icon name="trash" size={14} />
                <span>Reset local settings and reload</span>
              </button>

              <button
                type="button"
                className={styles.copyBtn}
                onClick={this.handleCopyDetails}
                title="Copy technical details to clipboard"
              >
                <Icon name="edit" size={14} />
                <span>{this.state.copied ? 'Copied to Clipboard!' : 'Copy details'}</span>
              </button>

              {!isRoute && (
                <button
                  type="button"
                  className={styles.homeBtn}
                  onClick={this.handleGoHome}
                >
                  Go to Home
                </button>
              )}
            </div>

            {error?.stack && (
              <details className={styles.detailsBlock}>
                <summary className={styles.detailsSummary}>Technical Stack Trace</summary>
                <pre className={styles.errorBox}>
                  {error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Route-level ErrorBoundary for wrapping <Outlet /> in Shell.jsx
 */
export function RouteErrorBoundary({ children }) {
  return <ErrorBoundary isRoute>{children}</ErrorBoundary>;
}

export default ErrorBoundary;
